import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'

const fontFamily = isCanlan
  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
  : 'BarlowCondensed, Arial, Helvetica, sans-serif'

// Card-level styles
export const cardContainerStyle = css`
  border: 1px solid ${colors.SECONDARY_300};
  background-color: #212935;
  border-radius: 16px;
  padding: 16px;
  color: ${colors.NEUTRAL_0};
  max-width: 400px;
  margin: 0 auto;
`

export const headerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.SOFT_STEEL};
`

export const titleStyle = css`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 20px;
  color: ${colors.NEUTRAL_0};
  font-family: ${fontFamily};
`

export const viewMoreStyle = css`
  color: ${colors.PRIMARY};
  text-decoration: none;
  font-size: 14px;
  line-height: 17px;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px;
`

export const viewMoreTextStyle = css`
  color: ${colors.PRIMARY};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 17.4px;
  width: 100%;
`

// TeamsContainer styles
export const teamsContainerStyle = css`
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  gap: 8px;
  height: 80px;
`

export const teamInfoStyle = css`
  display: flex;
  flex: 1;
  text-align: center;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  height: 100%;
`

export const teamLogoStyle = css`
  width: 32px !important;
  height: 32px !important;
  border-radius: 4px;
  background-color: white;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.1);
  object-fit: contain;
`

export const teamNameStyle = css`
  color: ${colors.NEUTRAL_0};
  font-family: ${fontFamily};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  text-transform: capitalize;
  text-align: center;
`

export const vsTextStyle = css`
  font-size: 12px;
  line-height: 12px;
  color: ${colors.NEUTRAL_50};
  font-weight: 500;
  align-self: center;
`

// GameInfo styles
export const gameInfoStyle = css`
  font-size: 12px;
  line-height: 12px;
  color: ${colors.NEUTRAL_50};
  text-align: center;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const gameTimeStyle = css`
  color: ${colors.NEUTRAL_50};
  font-size: 12px;
  line-height: 12px;
  font-weight: 500;
  text-transform: capitalize;
`

// AttendanceQuestion styles
export const attendanceQuestionTextStyle = css`
  color: ${colors.NEUTRAL_0};
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  text-align: center;
  text-overflow: ellipsis;
  margin-bottom: 8px;
`

export const attendanceOptionsStyle = css`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`

export const attendanceButtonStyle = (
  bgColor: string,
  borderColor: string,
  textColor: string
) => css`
  background-color: ${bgColor};
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 8px 16px;
  gap: 8px;
  color: ${textColor};
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex: 1;
  cursor: pointer;
  width: 100%;
`

export const iconStyle = (color: string) => css`
  font-size: 16px;
  color: ${color || colors.NEUTRAL_50};
`

// AttendanceCounter styles
export const attendanceCounterContainerStyle = css`
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  background: transparent;
  margin-top: 8px;
`

export const titleAndTextContainerStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
`

export const separatorStyle = css`
  width: 1px;
  height: 44px;
  background-color: ${colors.SECONDARY_75};
`

export const counterTitleStyle = (color: string) => css`
  color: ${color};
  text-align: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 14.4px;
  letter-spacing: 1.44px;
  text-transform: uppercase;
`

export const counterTextStyle = (color: string) => css`
  align-self: stretch;
  color: ${color};
  text-align: center;
  font-family: ${fontFamily};
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 28.8px;
`

// === Per-team section styles (SN-5929 redesign) ===

export const teamSectionContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 16px;
`

export const teamSectionHeaderStyle = css`
  background-color: ${colors.SECONDARY_200};
  border-radius: 4px;
  padding: 4px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`

export const teamSectionHeaderTextStyle = css`
  color: ${colors.NEUTRAL_0};
  font-size: 10px;
  line-height: 12px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  margin: 0;
`

export const gameInfoInlineRowStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
  align-items: center;
  color: ${colors.NEUTRAL_50};
  font-size: 12px;
  line-height: 17.4px;
  font-weight: 500;
`

export const gameInfoBulletStyle = css`
  color: ${colors.NEUTRAL_50};
  font-size: 12px;
  line-height: 17.4px;
`

export const manageAttendanceButtonOutlinedStyle = css`
  display: flex;
  flex-direction: row;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid ${colors.NEUTRAL_0};
  background: transparent;
  width: 100%;
  color: ${colors.NEUTRAL_0};
  font-size: 14px;
  font-weight: 700;
  line-height: 20.3px;
  cursor: pointer;
`

export const roleButtonsRowStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
  align-items: stretch;
`

export const roleButtonColumnStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 0 0;
  min-width: 0;
`

export const roleButtonLabelAboveStyle = css`
  color: ${colors.NEUTRAL_0};
  font-size: 12px;
  line-height: 17.4px;
  font-weight: 400;
  margin: 0;
`

export const roleButtonContainerStyle = (isInteractive: boolean) => css`
  background-color: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  padding: 8px 8px 8px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  cursor: ${isInteractive ? 'pointer' : 'default'};
`

export const roleButtonNameStyle = css`
  flex: 1 0 0;
  min-width: 0;
  color: ${colors.NEUTRAL_0};
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
`

export const roleButtonSwapIconStyle = css`
  font-size: 20px;
  color: ${colors.PRIMARY};
  flex-shrink: 0;
`
